import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientHeader from "../../components/Client/ClientHeader";
import Footer from "../../components/Client/Footer";
import { useAuth } from "../../context/AuthContext";
import { useWishlist } from "../../context/WishlistContext";
import { useCart } from "../../context/CartContext";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName, localizedItemProductName } from "../../utils/localizedName";
import "../../components/Client/clientTheme.css";
import "./Wishlist.css";

export default function Wishlist() {
  const { t } = useTranslation(["wishlist", "common"]);
  const { language } = useLanguage();
  const { isAuthenticated, openLoginModal } = useAuth();
  const { items, removeItem } = useWishlist();
  const { addItem } = useCart();

  return (
    <div className="client-wishlist-page">
      <ClientHeader />

      <div className="client-wishlist-content">
        <h1 className="client-wishlist-title">{t("wishlistTitle")}</h1>

        {!isAuthenticated ? (
          <div className="client-wishlist-empty">
            <p>{t("loginPromptWishlist")}</p>
            <button type="button" className="client-wishlist-login-button" onClick={openLoginModal}>
              {t("common:logIn")}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="client-wishlist-empty">
            <p>{t("emptyWishlistMessage")}</p>
            <Link to="/products" className="client-wishlist-browse-link">
              {t("browseProductsLink")}
            </Link>
          </div>
        ) : (
          <div className="client-wishlist-grid">
            {items.map((item) => {
              const hasDiscount = item.discountPrice != null && item.discountPrice < item.price;
              const authenticityLabel = pickLocalizedName(
                language,
                item.authenticityValue,
                item.authenticityValueArabic,
                item.authenticityValueHebrew,
              );
              return (
                <div className="client-wishlist-card" key={item.wishlistId}>
                  <Link to={`/product/${item.productId}`} className="client-wishlist-card-image-link">
                    {item.productImageUrl ? (
                      <img src={item.productImageUrl} alt={localizedItemProductName(language, item)} className="client-wishlist-card-image" />
                    ) : (
                      <div className="client-wishlist-card-image client-wishlist-card-image-placeholder" />
                    )}
                    {authenticityLabel && (
                      <span className="client-wishlist-card-authenticity-badge">{authenticityLabel}</span>
                    )}
                  </Link>

                  <div className="client-wishlist-card-body">
                    <Link to={`/product/${item.productId}`} className="client-wishlist-card-name">
                      {localizedItemProductName(language, item)}
                    </Link>

                    <div className="client-wishlist-card-price-row">
                      {hasDiscount ? (
                        <>
                          <span className="client-wishlist-card-price-discount">{item.discountPrice.toFixed(2)} ₪</span>
                          <span className="client-wishlist-card-price-original">{item.price.toFixed(2)} ₪</span>
                        </>
                      ) : (
                        <span className="client-wishlist-card-price">{item.price.toFixed(2)} ₪</span>
                      )}
                    </div>

                    {item.stockQuantity <= 0 && <div className="client-wishlist-card-stock">{t("common:outOfStock")}</div>}

                    <div className="client-wishlist-card-actions">
                      <button
                        type="button"
                        className="client-wishlist-card-cart-button"
                        disabled={item.stockQuantity <= 0}
                        onClick={() => addItem(item.productId)}
                      >
                        {t("common:addToCart")}
                      </button>
                      <button
                        type="button"
                        className="client-wishlist-card-remove-button"
                        onClick={() => removeItem(item.productId)}
                      >
                        {t("common:remove")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
